<template>
  <div class="address-sync">
    <p class="address-sync__intro">
      {{ $t('projects.address_sync_intro', { count: projects.length }) }}
    </p>

    <div class="address-sync__new">
      <span class="address-sync__label">{{ $t('projects.address_sync_new_address') }}</span>
      <span class="address-sync__value">{{ formatAddress(leadAddress) }}</span>
    </div>

    <ul class="address-sync__list">
      <li v-for="project in projects" :key="project.id" class="address-sync__item">
        <div class="address-sync__item-main">
          <span class="address-sync__item-title">
            {{ projectDisplayTitle(project) }}
          </span>
          <span class="address-sync__item-address">
            {{ formatAddress(project.installation_address) || $t('projects.address_sync_empty') }}
          </span>
        </div>
        <span v-if="project.open_work_orders > 0" class="address-sync__item-meta">
          {{ $t('projects.address_sync_work_orders', { count: project.open_work_orders }) }}
        </span>
      </li>
    </ul>

    <p class="address-sync__note">{{ $t('projects.address_sync_note') }}</p>

    <div class="address-sync__actions">
      <Btn variant="outlined" @click="handleCancel">
        {{ $t('projects.address_sync_keep') }}
      </Btn>
      <Btn variant="primary" icon="check" :loading="saving" @click="handleApply">
        {{ $t('projects.address_sync_apply') }}
      </Btn>
    </div>
  </div>
</template>

<script>
  import { ref, getCurrentInstance } from 'vue';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { projectService } from '@/services/projectService';
  import { projectDisplayTitle } from '@/utils/projectTitle';
  import Btn from '@/components/partials/Btn.vue';

  export default {
    name: 'ProjectAddressSyncModal',

    components: {
      Btn,
    },

    props: {
      modalId: { type: [Number, String], default: null },
      leadId: { type: String, required: true },
      leadAddress: { type: Object, default: null },
      projects: { type: Array, default: () => [] },
      onApplied: { type: Function, default: null },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const uiStore = useUIStore();
      const toastStore = useToastStore();

      const saving = ref(false);

      const formatAddress = (address) => {
        if (!address) return '';
        const street = address.street || '';
        const houseNumber = address.house_number || address.houseNumber || '';
        const postalCode = address.postal_code || address.postalCode || '';
        const city = address.city || '';
        return `${street} ${houseNumber}, ${postalCode} ${city}`
          .replace(/\s+/g, ' ')
          .replace(/^,\s*|,\s*$/g, '')
          .trim();
      };

      const close = () => {
        if (props.modalId != null) uiStore.closeModal(props.modalId);
      };

      const handleCancel = () => close();

      const handleApply = async () => {
        if (saving.value) return;
        saving.value = true;
        try {
          const result = await projectService.applyLeadAddressSync(
            props.leadId,
            props.projects.map((project) => project.id)
          );
          if (result.failed?.length) {
            toastStore.addToast({
              type: 'error',
              message: $t('projects.address_sync_partial', {
                updated: result.updated?.length || 0,
                failed: result.failed.length,
              }),
              duration: 5000,
            });
          } else {
            toastStore.addToast({
              type: 'success',
              message: $t('projects.address_sync_success', {
                count: result.updated?.length || 0,
              }),
            });
          }
          if (props.onApplied) await props.onApplied(result);
          close();
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: error.response?.data?.error || $t('projects.address_sync_failed'),
          });
        } finally {
          saving.value = false;
        }
      };

      return {
        saving,
        projectDisplayTitle,
        formatAddress,
        handleCancel,
        handleApply,
      };
    },
  };
</script>

<style scoped lang="scss">
  .address-sync {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__intro {
      margin: 0;
      color: var(--slate-700);
    }

    &__new {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      padding: 0.75rem;
      border-radius: 0.5rem;
      background: var(--slate-50);
    }

    &__label {
      @include fontSize('sm');
      color: var(--slate-500);
    }

    &__value {
      font-weight: 600;
      color: var(--slate-900);
    }

    &__list {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    &__item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      padding: 0.625rem 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
    }

    &__item-main {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;
    }

    &__item-title {
      @include ellipsis(1);
      font-weight: 500;
      color: var(--slate-900);
    }

    &__item-address {
      @include fontSize('sm');
      color: var(--slate-500);
    }

    &__item-meta {
      @include fontSize('sm');
      flex-shrink: 0;
      color: var(--slate-500);
    }

    &__note {
      @include fontSize('sm');
      margin: 0;
      color: var(--slate-500);
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
    }
  }
</style>
